/* eslint-disable no-restricted-imports */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { Paginator } from '@/components/content/StoreLocator/parts/Paginator';
import { parseStoreHours } from '@/components/content/StoreLocator/parts/StoreHorario';
import { isStoreOpen } from '@/components/content/StoreLocator/parts/StoreHorario';
import { storeLocatorSideListAutoCompleteSX } from '@/components/content/StoreLocator/styles/SideList/autoComplete';
import { storeLocatorSideListCheckCircleRoundedIcon } from '@/components/content/StoreLocator/styles/SideList/checkCircleRoundedIcon';
import { styleDirection } from '@/components/content/StoreLocator/styles/SideList/checkCircleRoundedIcon';
import { storeLocatorSideListElementSX } from '@/components/content/StoreLocator/styles/SideList/element';
import { styleBtn } from '@/components/content/StoreLocator/styles/SideList/element';
import { styleSubText } from '@/components/content/StoreLocator/styles/SideList/element';
import { textMobileMex } from '@/components/content/StoreLocator/styles/SideList/element';
import {
	storeLocatorGridContainerSX,
	styleContainerList,
} from '@/components/content/StoreLocator/styles/SideList/gridContainer';
import { storeLocatorSideListNotInterestedIconSX } from '@/components/content/StoreLocator/styles/SideList/notInterestedIcon';
import { storeLocatorSideListSideListSX } from '@/components/content/StoreLocator/styles/SideList/sideList';
import { storeLocatorSideListTextImageCenterSX } from '@/components/content/StoreLocator/styles/SideList/textImageCenter';
import { storeLocatorSideListTitleTypographySX } from '@/components/content/StoreLocator/styles/SideList/titleTypography';
import { nameStyle } from '@/components/content/StoreLocator/styles/SideList/titleTypography';
import { useStoreLocator } from '@/data/Content/StoreLocator';
import { useLocalization } from '@/data/Localization';
import { ContentContext } from '@/data/context/content';
import { useStoreLocatorState } from '@/data/state/useStoreLocatorState';
import { combineSX } from '@/utils/combineSX';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import SearchIcon from '@mui/icons-material/Search';
import { tittleStyle } from '@/components/content/StoreLocator/styles/SideList/element';
import { useIsMobile } from '@/data/hooks/useIsMobile';
import { StoreLocatorInfoBoxMobile } from '@/components/content/StoreLocator/parts/InfoBoxMobile';
import { useEffect } from 'react';
import { StoreLocatorMarker } from '@/components/content/StoreLocator/parts/Marker';
import { ContentProvider } from '@/data/context/content';
import { storeLocatorMapContainerSXMobile } from '@/components/content/StoreLocator/styles/mapContainer';
import {
	Button,
	ButtonBase,
	Grid,
	InputAdornment,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	Skeleton,
	Stack,
	Typography,
} from '@mui/material';
import React, { FC, useContext } from 'react';
import { GoogleMap } from '@react-google-maps/api';
import { StoreLocatorAutocomplete } from '@/components/content/StoreLocator/parts/Autocomplete';
import { DpOutlinedInput } from '@/components/blocks/OutlinedInput';
import { useResizeObserverByElementId } from 'integration/hooks/useResizeObserverByElementId';

// eslint-disable-next-line complexity
export const StoreLocatorSideList: FC<{ isLoaded: boolean }> = ({ isLoaded }) => {
	const isMobile = useIsMobile();

	const { width: inputFrameWidth } = useResizeObserverByElementId(
		'store-locator-store-search-text-field-frame'
	);

	useEffect(() => {
		const style = document.createElement('style');
		style.innerHTML = `
            .pac-container {
                width: ${inputFrameWidth}px !important;
				shadow: 0px 1px 4px 2px rgba(0,0,0,0.02), 0px 2px 1px 0px rgba(0,0,0,0.03);
            }
			.pac-item {
				border: none;
			}
        `;
		document.head.appendChild(style);

		return () => {
			document.head.removeChild(style);
		};
	}, [inputFrameWidth]);

	const {
		searchTextFieldRef,
		currentLocation,
		locator,
		searchTerm,
		clickedIndex,
		findNearStore,
		firstSuggestion,
		searchBoxOnLoad,
		onPlaceChanged,
		handleSearch,
		handleKeyDown,
		onListItemClick,
		clearSearch,
		calcDistance,
		mapCenter,
		showDirection,
		directionsCallback,
		destination,
		directionResults,
		mapOnLoad,
		nearStores,
		isLoading,
		setSearchTerm,
		mobilePaginatorPage,
		setMobilePaginatorPage,
		...rest
	} = useContext(ContentContext) as ReturnType<typeof useStoreLocator>;
	const localization = useLocalization('StoreLocator');
	const { storeLocator } = useStoreLocatorState();

	return (
		<Stack
			sx={combineSX([
				storeLocatorGridContainerSX,
				styleContainerList,
				{
					paddingLeft: isMobile ? '20px' : '15%',
					paddingRight: isMobile ? '20px' : '5%',
					paddingTop: isMobile ? '24px' : '31px',
				},
			])}
		>
			<Typography variant="h1" sx={tittleStyle}>
				{localization.title.t()}
			</Typography>

			<ListItem
				sx={{
					paddingLeft: isMobile ? '20px' : '0px',
					paddingRight: isMobile ? '20px' : '0px',
				}}
				disablePadding
			>
				<Grid
					container
					justifyContent="space-between"
					flexDirection="column"
					alignItems="stretch"
					spacing={1}
				>
					<Grid item flex={1}>
						<ListItemText
							sx={storeLocatorSideListElementSX}
							primary={
								<Typography
									variant="subtitle2"
									sx={combineSX([storeLocatorSideListTitleTypographySX, styleSubText])}
								>
									{localization.searchAPickUpStore.t()}
								</Typography>
							}
						/>
					</Grid>
					<Grid item flex={1}>
						<Button
							variant="contained"
							onClick={findNearStore}
							sx={combineSX([storeLocatorSideListElementSX, styleBtn])}
							data-testid="button-store-locator-find-nearest-store"
							id="button-store-locator-find-nearest-store"
							disabled={!isLoaded}
						>
							{localization.findNearestStore.t()}
						</Button>
					</Grid>
				</Grid>
			</ListItem>
			{isMobile ? (
				<Grid sx={textMobileMex}>
					<Typography
						sx={{
							margin: '16px 0 0',
							fontSize: 'var(--font-size-lg)',
							color: '#2C2C2C',
							textTransform: 'uppercase',
							fontWeight: 800,
						}}
					>
						{'Estamos en todo México'}
					</Typography>{' '}
				</Grid>
			) : null}
			<ListItem disablePadding id="store-locator-store-search-text-field-frame">
				{isLoaded ? (
					<StoreLocatorAutocomplete
						sx={storeLocatorSideListAutoCompleteSX}
						onLoad={searchBoxOnLoad}
						onPlaceChanged={onPlaceChanged}
						options={{
							componentRestrictions: { country: 'mx' },
						}}
					>
						<DpOutlinedInput
							fullWidth
							autoFocus
							autoComplete="off"
							placeholder={localization.searchStore.t()}
							name="searchTerm"
							inputRef={searchTextFieldRef}
							data-testid="store-locator-store-search-text-field"
							id="store-locator-store-search-text-field"
							onKeyUp={handleKeyDown}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										{searchTerm.length > 0 ? (
											<ButtonBase onClick={clearSearch}>{'Borrar'}</ButtonBase>
										) : (
											<SearchIcon
												onClick={handleSearch}
												sx={{
													fontSize: 'var(--font-size-xxl)',
													color: '#2682E7',
													cursor: 'pointer',
												}}
											/>
										)}
									</InputAdornment>
								),
							}}
						/>
					</StoreLocatorAutocomplete>
				) : (
					<Skeleton
						variant="rounded"
						width="100%"
						height="56px"
						sx={storeLocatorSideListAutoCompleteSX}
					/>
				)}
			</ListItem>
			{locator?.storeList?.length > 0 && !locator.noSearch && !isLoading ? (
				<ListItem disablePadding>
					<Grid container justifyContent="space-between" alignItems="flex-end" spacing={1}>
						<Grid item flex={1}>
							<ListItemText
								sx={combineSX([storeLocatorSideListElementSX, { whiteSpace: 'normal' }])}
								primary={
									!searchTerm && !nearStores
										? `${locator.storeList.length} tiendas en todo México`
										: `${locator.storeList.length} resultados cerca de ${
												nearStores ? 'mi' : `"${searchTerm}"`
										  }`
								}
							/>
						</Grid>
					</Grid>
				</ListItem>
			) : null}
			<Stack sx={storeLocatorSideListSideListSX}>
				<List>
					{isMobile ? (
						<>
							{currentLocation && !isLoading ? (
								<Paginator page={mobilePaginatorPage} setPage={setMobilePaginatorPage}>
									{locator?.storeList?.map((store, index) =>
										store?.coordinates ? (
											<Grid key={index} sx={{ mb: '15px' }}>
												<ListItemButton
													key={store.id}
													data-testid={`pickup-store-${store?.storeName.toLowerCase()}-list-button`}
													id={`pickup-store-${store?.storeName.toLowerCase()}-list-button`}
													onClick={(e) => onListItemClick(e, index)}
													divider
													sx={{
														border: 'none',
														display: 'flex',
														flexDirection: 'column',
														backgroundColor: '#fff',
														padding: '0',
													}}
												>
													{isLoaded ? (
														<ContentProvider
															value={{
																locator,
																showDirection,
																mapCenter,
																clickedIndex,
																searchTerm,
																searchTextFieldRef,
																currentLocation,
																findNearStore,
																searchBoxOnLoad,
																onPlaceChanged,
																onListItemClick,
																clearSearch,
																...rest,
															}}
														>
															<Grid item xs sx={isMobile ? storeLocatorMapContainerSXMobile : null}>
																<GoogleMap
																	mapContainerStyle={{
																		height: '122px',
																		width: '100%',
																	}}
																	zoom={15}
																	center={store.coordinates}
																	onLoad={mapOnLoad}
																>
																	<StoreLocatorMarker store={store} index={index} />
																</GoogleMap>
															</Grid>
														</ContentProvider>
													) : (
														<Skeleton variant="rectangular" width="100%" height="122px" />
													)}
													<ListItemText
														primary={
															<Typography variant="h4">
																<Typography sx={nameStyle}> {store.storeName} </Typography>
																{store.id === storeLocator.selectedStore?.id ? (
																	<CheckCircleRoundedIcon
																		sx={storeLocatorSideListCheckCircleRoundedIcon}
																	/>
																) : null}
															</Typography>
														}
														secondary={
															<Typography variant="caption" sx={styleDirection}>
																<Typography>
																	{(() => {
																		try {
																			const storeHours = parseStoreHours(store.attributes[0].value);
																			const openStatus = isStoreOpen(storeHours);
																			const isOpen = openStatus.startsWith('Abierto');

																			return (
																				<>
																					{isOpen ? (
																						<Typography component="span">
																							<Typography component="span" sx={{ color: 'green' }}>
																								{'Abierto'}
																							</Typography>
																							{' - hasta '} {storeHours.weekdaysClose}
																						</Typography>
																					) : (
																						<Typography component="span">
																							<Typography component="span" sx={{ color: 'red' }}>
																								{'Cerrado'}
																							</Typography>
																							{' - Abre a '}
																							{storeHours.weekdaysOpen}
																						</Typography>
																					)}
																				</>
																			);
																		} catch (error) {
																			return (
																				<Typography component="span">
																					{store.attributes[0].value}
																				</Typography>
																			);
																		}
																	})()}
																</Typography>
																{store.storeFullAddress}
															</Typography>
														}
													></ListItemText>
												</ListItemButton>
												<StoreLocatorInfoBoxMobile store={store} />
											</Grid>
										) : null
									)}
								</Paginator>
							) : (
								<Stack
									sx={{
										display: 'flex',
										flexDirection: 'column',
										gap: '10px',
										marginTop: '20px',
									}}
								>
									<Skeleton variant="rounded" width="100%" height="120px" />
									<Skeleton variant="rounded" width="100%" height="120px" />
									<Skeleton variant="rounded" width="100%" height="120px" />
								</Stack>
							)}
						</>
					) : null}

					{!isMobile ? (
						<React.Fragment>
							{currentLocation && !isLoading ? (
								locator?.storeList?.map((store, index) =>
									store?.coordinates ? (
										<ListItemButton
											key={index}
											data-testid={`pickup-store-${store?.storeName.toLowerCase()}-list-button`}
											id={`pickup-store-${store?.storeName.toLowerCase()}-list-button`}
											onClick={(e) => onListItemClick(e, index)}
											divider
											sx={{
												backgroundColor: '#fff',
												marginBottom: '8px',
												boxShadow: 'none',
												borderRadius: '4px',
												border: clickedIndex === index ? '1px solid #2C2C2C' : '1px solid #fff',
												'&:hover': {
													border: '1px solid #2C2C2C',
												},
											}}
										>
											<ListItemText
												primary={
													<Typography variant="h4" sx={nameStyle}>
														{store.storeName}
														{store.id === storeLocator.selectedStore?.id ? (
															<CheckCircleRoundedIcon
																sx={storeLocatorSideListCheckCircleRoundedIcon}
															/>
														) : null}
													</Typography>
												}
												secondary={
													<Typography variant="caption" sx={styleDirection}>
														<Typography>
															{(() => {
																try {
																	const storeHours = parseStoreHours(store.attributes[0].value);
																	const openStatus = isStoreOpen(storeHours);
																	const isOpen = openStatus.startsWith('Abierto');

																	return (
																		<>
																			{isOpen ? (
																				<Typography component="span">
																					<Typography component="span" sx={{ color: 'green' }}>
																						{'Abierto'}
																					</Typography>
																					{' - hasta las '} {storeHours.weekdaysClose}
																				</Typography>
																			) : (
																				<Typography component="span">
																					<Typography component="span" sx={{ color: 'red' }}>
																						{'Cerrado'}
																					</Typography>
																					{' - Abre a '}
																					{storeHours.weekdaysOpen}
																				</Typography>
																			)}
																		</>
																	);
																} catch (error) {
																	return (
																		<Typography component="span">
																			{store.attributes[0].value}
																		</Typography>
																	);
																}
															})()}
														</Typography>
														{store.storeFullAddress}
													</Typography>
												}
											></ListItemText>
										</ListItemButton>
									) : null
								)
							) : (
								<Stack
									sx={{
										display: 'flex',
										flexDirection: 'column',
										gap: '10px',
										marginTop: '20px',
									}}
								>
									<Skeleton variant="rounded" width="100%" height="160px" />
									<Skeleton variant="rounded" width="100%" height="160px" />
									<Skeleton variant="rounded" width="100%" height="160px" />
								</Stack>
							)}
						</React.Fragment>
					) : null}
				</List>
			</Stack>
			{locator?.storeList?.length === 0 && !locator.noSearch && !isLoading ? (
				<Stack
					alignItems="center"
					justifyContent="center"
					flexGrow={1}
					sx={storeLocatorSideListTextImageCenterSX}
				>
					<NotInterestedIcon htmlColor="lightgrey" sx={storeLocatorSideListNotInterestedIconSX} />
					<Typography variant="subtitle2">
						{searchTerm
							? localization.noResults.t({ p: `"${searchTerm}"` })
							: localization.noResultsNearYou.t()}
					</Typography>
				</Stack>
			) : null}
		</Stack>
	);
};
